import Swal from 'sweetalert2';

const escapeHtml = (value = '') =>
    String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

const baseCustomClass = {
    container: 'ecx-swal-container',
    popup: 'ecx-swal',
    title: 'ecx-swal__title',
    htmlContainer: 'ecx-swal__html',
    actions: 'ecx-swal__actions',
    confirmButton: 'btn rounded-pill px-3 ecx-swal__btn',
    cancelButton: 'btn btn-outline-secondary rounded-pill px-3 ecx-swal__btn ecx-swal__btn--cancel',
    loader: 'ecx-swal__loader',
    validationMessage: 'ecx-swal__validation',
};

const buildCancelButtonHtml = (text = 'Cancel') =>
    `<i class="bi bi-x-lg me-1" aria-hidden="true"></i>${escapeHtml(text)}`;

const buildConfirmButtonHtml = (iconClass, text) =>
    `<i class="bi ${iconClass} me-1" aria-hidden="true"></i>${escapeHtml(text)}`;

const EcxSwal = Swal.mixin({
    buttonsStyling: false,
    customClass: baseCustomClass,
    focusCancel: true,
});

const buildDeleteHtml = ({ itemName, description }) => `
    <div class="ecx-swal-delete">
        <span class="ecx-swal-delete__icon" aria-hidden="true">
            <i class="bi bi-trash3"></i>
        </span>
        <p class="ecx-swal-delete__message">
            Are you sure you want to delete <strong>${escapeHtml(itemName)}</strong>?
        </p>
        ${
            description
                ? `<p class="ecx-swal-delete__hint">${escapeHtml(description)}</p>`
                : '<p class="ecx-swal-delete__hint">This action cannot be undone.</p>'
        }
    </div>
`;

export const confirmDelete = ({
    title = 'Delete Media?',
    itemName,
    description,
    confirmText = 'Delete',
    cancelText = 'Cancel',
    onConfirm,
}) =>
    EcxSwal.fire({
        title,
        html: buildDeleteHtml({ itemName, description }),
        showCancelButton: true,
        confirmButtonText: buildConfirmButtonHtml('bi-trash', confirmText),
        cancelButtonText: buildCancelButtonHtml(cancelText),
        cancelButtonAriaLabel: cancelText,
        customClass: {
            ...baseCustomClass,
            confirmButton: 'btn btn-danger rounded-pill px-3 ecx-swal__btn ecx-swal__btn--danger',
        },
        showLoaderOnConfirm: Boolean(onConfirm),
        allowOutsideClick: () => !Swal.isLoading(),
        preConfirm: onConfirm
            ? () =>
                  Promise.resolve(onConfirm()).catch((error) => {
                      const message =
                          error?.response?.data?.message ||
                          error?.response?.data?.error ||
                          error?.message ||
                          'Something went wrong. Please try again.';
                      Swal.showValidationMessage(message);
                      return Promise.reject(error);
                  })
            : undefined,
    });

export const confirmAction = ({
    title,
    html,
    iconClass = 'bi bi-question-circle',
    iconTone = 'primary',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    confirmTone = 'primary',
    onConfirm,
}) =>
    EcxSwal.fire({
        title,
        html: `
            <div class="ecx-swal-confirm">
                <span class="ecx-swal-confirm__icon ecx-swal-confirm__icon--${iconTone}" aria-hidden="true">
                    <i class="${iconClass}"></i>
                </span>
                <div class="ecx-swal-confirm__content">${html}</div>
            </div>
        `,
        showCancelButton: true,
        confirmButtonText: buildConfirmButtonHtml('bi-check-lg', confirmText),
        cancelButtonText: buildCancelButtonHtml(cancelText),
        cancelButtonAriaLabel: cancelText,
        customClass: {
            ...baseCustomClass,
            confirmButton: `btn btn-${confirmTone === 'danger' ? 'danger' : 'light-primary'} rounded-pill px-3 ecx-swal__btn ecx-swal__btn--${confirmTone}`,
        },
        showLoaderOnConfirm: Boolean(onConfirm),
        allowOutsideClick: () => !Swal.isLoading(),
        preConfirm: onConfirm
            ? () =>
                  Promise.resolve(onConfirm()).catch((error) => {
                      const message =
                          error?.response?.data?.message ||
                          error?.response?.data?.error ||
                          error?.message ||
                          'Something went wrong. Please try again.';
                      Swal.showValidationMessage(message);
                      return Promise.reject(error);
                  })
            : undefined,
    });

export const showSuccess = ({ title = 'Success', text, timer = 3000 } = {}) =>
    EcxSwal.fire({
        title,
        text,
        icon: 'success',
        showCancelButton: false,
        focusCancel: false,
        focusConfirm: true,
        confirmButtonText: buildConfirmButtonHtml('bi-check-lg', 'OK'),
        customClass: {
            ...baseCustomClass,
            confirmButton: 'btn btn-light-primary rounded-pill px-3 ecx-swal__btn',
        },
        timer,
        timerProgressBar: Boolean(timer),
    });

export const showError = ({ title = 'Error', text } = {}) =>
    EcxSwal.fire({
        title,
        text,
        icon: 'error',
        showCancelButton: false,
        focusCancel: false,
        focusConfirm: true,
        confirmButtonText: buildConfirmButtonHtml('bi-x-lg', 'Close'),
        customClass: {
            ...baseCustomClass,
            confirmButton: 'btn btn-outline-secondary rounded-pill px-3 ecx-swal__btn ecx-swal__btn--cancel',
        },
    });

export const showInfo = ({ title = 'Notice', text, timer = 3000 } = {}) =>
    EcxSwal.fire({
        title,
        text,
        icon: 'info',
        showCancelButton: false,
        focusCancel: false,
        focusConfirm: true,
        confirmButtonText: buildConfirmButtonHtml('bi-info-circle', 'OK'),
        customClass: {
            ...baseCustomClass,
            confirmButton: 'btn btn-light-primary rounded-pill px-3 ecx-swal__btn',
        },
        timer,
        timerProgressBar: Boolean(timer),
    });

export default EcxSwal;
